import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useRef,
  useState,
  type ReactNode,
} from "react";
import {
  categoryLargeList as fallbackCategoryLargeList,
  categorySmallList as fallbackCategorySmallList,
} from "./commonConstant";

// 汎用マスタの区分コード（hanyo_cd）。002=収支項目（収支区分・項目大・項目小の組）。
const SHUSHI_KOUMOKU_CD = "000002";

export interface MasterItem {
  hanyoCd: string;
  hanyoKoumokuCd: string;
  description: string | null;
  hanyoMoji1: string | null;
  hanyoMoji2: string | null;
  hanyoMoji3: string | null;
}

interface CategoryOption {
  label: string;
  type: string;
  category: string;
}

interface MasterContextValue {
  items: MasterItem[];
  categoryLargeList: CategoryOption[];
  categorySmallList: CategoryOption[];
  isLoading: boolean;
  isFallback: boolean;
  refetch: () => Promise<boolean>;
}

const MasterContext = createContext<MasterContextValue | undefined>(undefined);

const isMasterItem = (value: unknown): value is MasterItem => {
  if (typeof value !== "object" || value === null) return false;
  const v = value as Record<string, unknown>;
  return typeof v.hanyoCd === "string" && typeof v.hanyoKoumokuCd === "string";
};

// 項目（大）のグルーピングキー。収支区分をまたいだ同名の項目大を区別するため両方を含める。
export const buildCategoryLargeKey = (hanyoMoji1: string, hanyoMoji2: string): string =>
  `${hanyoMoji1}|${hanyoMoji2}`;

type ShushiKoumokuRow = MasterItem & { hanyoMoji1: string; hanyoMoji2: string };

const hasCategoryFields = (item: MasterItem): item is ShushiKoumokuRow =>
  Boolean(item.hanyoMoji1 && item.hanyoMoji2);

const hasSmallLabel = (
  item: ShushiKoumokuRow,
): item is ShushiKoumokuRow & { hanyoMoji3: string } => Boolean(item.hanyoMoji3);

// hanyo_cd=000002の行から、項目（小）は1行=1件、項目（大）は収支+項目大の重複を除いて導出する
const deriveCategoryLists = (items: MasterItem[]) => {
  const rows = items
    .filter((item) => item.hanyoCd === SHUSHI_KOUMOKU_CD)
    .filter(hasCategoryFields);

  const categorySmall: CategoryOption[] = rows.filter(hasSmallLabel).map((item) => ({
    label: item.hanyoMoji3,
    type: item.hanyoMoji1,
    category: item.hanyoMoji2,
  }));

  const seen = new Set<string>();
  const categoryLarge: CategoryOption[] = [];
  rows.forEach((item) => {
    const key = buildCategoryLargeKey(item.hanyoMoji1, item.hanyoMoji2);
    if (seen.has(key)) return;
    seen.add(key);
    categoryLarge.push({
      label: item.hanyoMoji2,
      type: item.hanyoMoji1,
      category: item.hanyoMoji2,
    });
  });

  return { categoryLarge, categorySmall };
};

export const MasterProvider = ({ children }: { children: ReactNode }) => {
  const [items, setItems] = useState<MasterItem[]>([]);
  const [status, setStatus] = useState<"loading" | "loaded" | "error">("loading");
  const hasLoadedOnceRef = useRef(false);

  const fetchMaster = useCallback(async (): Promise<boolean> => {
    setStatus("loading");
    try {
      const res = await fetch(`${process.env.REACT_APP_API_BASE_URL}/api/master`, {
        credentials: "include",
      });
      if (!res.ok) throw new Error(`master fetch failed: ${res.status}`);
      const data: unknown = await res.json();
      if (!Array.isArray(data) || !data.every(isMasterItem)) {
        throw new Error("master fetch returned unexpected shape");
      }
      setItems(data);
      setStatus("loaded");
      hasLoadedOnceRef.current = true;
      return true;
    } catch (err) {
      console.error("汎用マスタの取得に失敗しました。", err);
      // 初回ロード失敗時のみ空にする。再取得（refetch）の失敗では、
      // 直前に取得できていた一覧を維持し「全項目が消えた」ように見えることを防ぐ。
      if (!hasLoadedOnceRef.current) {
        setItems([]);
      }
      setStatus("error");
      return false;
    }
  }, []);

  useEffect(() => {
    fetchMaster();
  }, [fetchMaster]);

  const isFallback = status !== "loaded" && !hasLoadedOnceRef.current;
  const derived = deriveCategoryLists(items);

  const value: MasterContextValue = {
    items,
    categoryLargeList: isFallback ? fallbackCategoryLargeList : derived.categoryLarge,
    categorySmallList: isFallback ? fallbackCategorySmallList : derived.categorySmall,
    isLoading: status === "loading",
    isFallback,
    refetch: fetchMaster,
  };

  return <MasterContext.Provider value={value}>{children}</MasterContext.Provider>;
};

export const useMaster = (): MasterContextValue => {
  const ctx = useContext(MasterContext);
  if (!ctx) {
    throw new Error("useMaster must be used within a MasterProvider");
  }
  return ctx;
};
